import React, { ReactNode } from 'react';

/**
 * The booking flow's price surface: what the step costs, the way forward,
 * and the fine print that qualifies both — one panel, stuck to the bottom of
 * the viewport while the page scrolls.
 *
 * Introduced on the Boxes catalogue (Phase 4 Track 4.5) and lifted to a
 * primitive when CollectionDelivery needed the same thing (Track 4.8).
 * DESIGN_LANGUAGE.md §6 — consume the primitive, don't roll bespoke: the two
 * pages had otherwise been about to carry two panels that looked alike and
 * drifted apart.
 *
 * The rule the panel exists to enforce is "one price surface per page". Both
 * pages previously had two — a figure row at the very foot of the document
 * plus, on a phone, a `position: fixed` bar with no matching body padding
 * that sat on top of the last row of content.
 */

/** The primary action's trailing arrow. Shared so both pages draw the same one. */
export function SummaryArrow(): JSX.Element {
  return (
    <svg
      className="booking-summary__arrow"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

export interface SummaryFigure {
  label: string;
  value: string;
  /**
   * The figure the customer is actually deciding on. Mark the total, or —
   * where two figures are peers rather than one being a sub-total of the
   * other (materials now vs rent every month) — mark both.
   */
  headline?: boolean;
}

interface BookingSummaryProps {
  figures: SummaryFigure[];
  /** The primary control. A `<Button type="submit">` in every current use. */
  action: ReactNode;
  /**
   * One line on where the customer stands — what is still outstanding, or
   * that they are ready to go on. Wire the action's `aria-describedby` to
   * `statusId` so the reason a step is not finished reaches assistive tech.
   */
  status?: ReactNode;
  statusId?: string;
  /** `alert` tints the status line so an outstanding requirement reads as one. */
  statusTone?: 'neutral' | 'alert';
  /** Fine print. Sits with the figures it qualifies, not in a block below them. */
  notes?: ReactNode;
  className?: string;
}

function Figure({ label, value, headline }: SummaryFigure): JSX.Element {
  return (
    <div
      className={`booking-summary__figure${
        headline ? ' booking-summary__figure--headline' : ''
      }`}
    >
      <span className="booking-summary__label">{label}</span>
      <span className="booking-summary__value">{value}</span>
    </div>
  );
}

export default function BookingSummary({
  figures,
  action,
  status,
  statusId,
  statusTone = 'neutral',
  notes,
  className,
}: BookingSummaryProps): JSX.Element {
  return (
    <div className={`booking-summary${className ? ` ${className}` : ''}`}>
      <div className="booking-summary__inner">
        <div className="booking-summary__figures">
          {figures.map((figure) => (
            <Figure key={figure.label} {...figure} />
          ))}
        </div>

        <div className="booking-summary__action">{action}</div>

        {(status || notes) && (
          <div className="booking-summary__meta">
            {status && (
              <p
                className={`booking-summary__status booking-summary__status--${statusTone}`}
                id={statusId}
                aria-live="polite"
              >
                {status}
              </p>
            )}
            {notes && <p className="booking-summary__notes">{notes}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
